import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Upload, Loader2, FileText, FileCode2, Image as ImageIcon, CheckCircle2, AlertTriangle,
  Save, Trash2, Link2, RotateCcw, FileSearch,
} from "lucide-react";
import { toast } from "sonner";

const NUM = (n) => (n == null ? "" : Number(n).toLocaleString("es-PY", { maximumFractionDigits: 2 }));
const CAMPOS = [
  { k: "numero", label: "N° de factura" },
  { k: "cdc", label: "CDC" },
  { k: "fecha", label: "Fecha", type: "date" },
  { k: "condicion", label: "Condición" },
  { k: "moneda", label: "Moneda" },
  { k: "timbrado", label: "Timbrado" },
  { k: "ruc_emisor", label: "RUC emisor" },
  { k: "nombre_emisor", label: "Razón social emisor" },
  { k: "ruc_receptor", label: "RUC receptor" },
  { k: "nombre_receptor", label: "Razón social receptor" },
];
const MONTOS = [
  { k: "subtotal_exentas", label: "Exentas" },
  { k: "subtotal_5", label: "Gravadas 5%" },
  { k: "subtotal_10", label: "Gravadas 10%" },
  { k: "iva_5", label: "IVA 5%" },
  { k: "iva_10", label: "IVA 10%" },
  { k: "total", label: "Total" },
];

const confCls = (c) =>
  c == null ? "border-slate-200" : c >= 0.85 ? "border-emerald-400 bg-emerald-50/40"
    : c >= 0.6 ? "border-amber-400 bg-amber-50/40" : "border-red-400 bg-red-50/40";
const confDot = (c) =>
  c == null ? "bg-slate-300" : c >= 0.85 ? "bg-emerald-500" : c >= 0.6 ? "bg-amber-500" : "bg-red-500";

function Field({ label, value, conf, onChange, type, testid }) {
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold text-[#4b6355]">
        <span className={`h-2 w-2 rounded-full ${confDot(conf)}`} /> {label}
      </label>
      <input
        type={type || "text"}
        data-testid={testid}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={`h-9 w-full rounded-lg border-2 bg-white px-3 text-sm outline-none focus:border-[#14532d] ${confCls(conf)}`}
      />
    </div>
  );
}

export default function GranosFacturas({ user }) {
  const [tab, setTab] = useState("cargar");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);       // { ...fields }
  const [conf, setConf] = useState({});
  const [vals, setVals] = useState([]);
  const [meta, setMeta] = useState(null);         // storage_path, file_kind, fuente, original_filename
  const [preview, setPreview] = useState(null);   // { url, kind }
  const [contrato, setContrato] = useState("");
  const [contratos, setContratos] = useState([]);
  const [hist, setHist] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/granos/contratos").then((r) => setContratos(r.data.contratos || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (tab === "historial") api.get("/granos/facturas").then((r) => setHist(r.data.facturas || [])).catch(() => {});
  }, [tab]);

  const loadPreview = async (path, kind) => {
    if (!path || kind === "xml") { setPreview(null); return; }
    try {
      const r = await api.get("/granos/facturas/file", { params: { path }, responseType: "blob" });
      setPreview({ url: URL.createObjectURL(r.data), kind });
    } catch { setPreview(null); }
  };

  const onFile = async (f) => {
    if (!f) return;
    setBusy(true); setDraft(null); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/granos/facturas/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = r.data.extracted || {};
      setDraft(ex);
      setConf(r.data.confidence || {});
      setVals(r.data.validations || []);
      setMeta({ storage_path: r.data.storage_path, file_kind: r.data.file_kind, fuente: r.data.fuente, original_filename: r.data.original_filename });
      // intenta vincular contrato por RUC/nombre receptor
      const guess = (contratos || []).find((c) => (ex.nombre_receptor || "").toUpperCase().includes((c.productor || "").toUpperCase().slice(0, 8)));
      setContrato(guess ? `${guess.cultivo}||${guess.contrato}||${guess.productor}` : "");
      loadPreview(r.data.storage_path, r.data.file_kind);
      toast.success("Factura leída. Revisá los datos resaltados.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo leer la factura");
    } finally { setBusy(false); }
  };

  const setF = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!draft) return;
    if (!contrato) { toast.error("Vinculá la factura a un contrato de granos."); return; }
    const [cultivo, nro, productor] = contrato.split("||");
    setBusy(true);
    try {
      await api.post("/granos/facturas", {
        ...draft, contrato: nro, cliente: productor, cultivo,
        storage_path: meta?.storage_path, original_filename: meta?.original_filename,
        file_kind: meta?.file_kind, fuente: meta?.fuente,
      });
      toast.success("Factura guardada y vinculada al contrato.");
      setDraft(null); setPreview(null); setContrato("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  const delFactura = async (id) => {
    try { await api.delete(`/granos/facturas/${id}`); setHist((h) => h.filter((x) => x.id !== id)); toast.success("Factura eliminada"); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const FuenteBadge = () => meta && (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#e4f7ea] px-3 py-1 text-xs font-bold text-[#14532d]">
      {meta.file_kind === "xml" ? <FileCode2 className="h-3.5 w-3.5" /> : meta.file_kind === "pdf" ? <FileText className="h-3.5 w-3.5" /> : <ImageIcon className="h-3.5 w-3.5" />}
      {meta.fuente === "xml" ? "Leído del XML SIFEN" : meta.fuente === "pdf" ? "Leído del PDF (texto)" : "Leído por IA de visión"}
    </span>
  );

  return (
    <div className="space-y-5" data-testid="granos-facturas">
      <div className="inline-flex gap-1.5 rounded-2xl bg-[#e0e7df] p-1">
        {[["cargar", "Cargar factura"], ["historial", "Historial"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`facturas-tab-${k}`}
            className={`min-h-9 rounded-xl px-4 text-sm font-bold transition-colors ${tab === k ? "bg-[#14532d] text-white" : "text-[#14231a]"}`}>{l}</button>
        ))}
      </div>

      {tab === "cargar" && !draft && (
        <label className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[#c3cfc2] bg-white py-16 text-center hover:bg-[#f7faf6]"
          data-testid="facturas-dropzone">
          {busy ? <Loader2 className="h-10 w-10 animate-spin text-[#14532d]" /> : <Upload className="h-10 w-10 text-[#14532d]" />}
          <div>
            <p className="text-base font-bold text-[#14231a]">{busy ? "Leyendo factura…" : "Subí una factura"}</p>
            <p className="text-sm text-slate-500">XML SIFEN · PDF · foto o escaneo (JPG/PNG)</p>
          </div>
          <input ref={fileRef} type="file" accept=".xml,.pdf,image/*" className="hidden" data-testid="facturas-file-input"
            onChange={(e) => onFile(e.target.files?.[0])} disabled={busy} />
        </label>
      )}

      {tab === "cargar" && draft && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2" data-testid="facturas-review">
          {/* Documento */}
          <div className="rounded-2xl bg-white p-4 shadow-[0_2px_10px_rgba(20,83,45,0.08)]">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-[15px] font-extrabold text-[#14231a]"><FileSearch className="h-4 w-4 text-[#14532d]" /> Documento</h3>
              <Button size="sm" variant="outline" onClick={() => { setDraft(null); setPreview(null); if (fileRef.current) fileRef.current.value = ""; }} data-testid="facturas-otra">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Otra
              </Button>
            </div>
            <div className="mb-3"><FuenteBadge /></div>
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50" style={{ minHeight: 280 }}>
              {preview?.kind === "image" && <img src={preview.url} alt="factura" className="w-full object-contain" style={{ maxHeight: 520 }} />}
              {preview?.kind === "pdf" && <iframe title="factura" src={preview.url} className="w-full" style={{ height: 520 }} />}
              {(!preview || meta?.file_kind === "xml") && (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-400">
                  <FileCode2 className="h-10 w-10" />
                  <span className="text-sm">{meta?.original_filename}</span>
                </div>
              )}
            </div>
            {draft.items?.length > 0 && (
              <div className="mt-4">
                <p className="mb-1 text-xs font-bold text-[#4b6355]">Ítems ({draft.items.length})</p>
                <div className="max-h-48 overflow-auto rounded-lg border border-slate-100">
                  <table className="w-full text-[12px]">
                    <thead className="bg-[#f1f4f0] text-slate-500"><tr>{["Descripción", "Cant.", "P. unit.", "Total"].map((h, i) => <th key={i} className={`px-2 py-1 ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
                    <tbody>
                      {draft.items.map((it, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-2 py-1">{it.descripcion || "—"}</td>
                          <td className="px-2 py-1 text-right">{NUM(it.cantidad)}</td>
                          <td className="px-2 py-1 text-right">{NUM(it.precio_unitario)}</td>
                          <td className="px-2 py-1 text-right font-semibold">{NUM(it.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Campos editables */}
          <div className="rounded-2xl bg-white p-4 shadow-[0_2px_10px_rgba(20,83,45,0.08)]">
            <h3 className="mb-3 text-[15px] font-extrabold text-[#14231a]">Datos de la factura</h3>

            {/* Vincular contrato */}
            <div className="mb-4 rounded-xl border-2 border-[#14532d]/20 bg-[#f7faf6] p-3">
              <label className="mb-1 flex items-center gap-1.5 text-[11px] font-bold text-[#14532d]"><Link2 className="h-3.5 w-3.5" /> Vincular a contrato de granos *</label>
              <input list="contratos-list" value={contrato ? contrato.split("||")[2] + " · " + contrato.split("||")[1] : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const c = contratos.find((x) => `${x.productor} · ${x.contrato}` === v || x.contrato === v);
                  setContrato(c ? `${c.cultivo}||${c.contrato}||${c.productor}` : "");
                }}
                placeholder="Buscar por productor o N° de contrato…"
                className="h-9 w-full rounded-lg border-2 border-[#c3cfc2] bg-white px-3 text-sm outline-none focus:border-[#14532d]"
                data-testid="facturas-contrato-input" />
              <datalist id="contratos-list">
                {contratos.map((c) => <option key={`${c.cultivo}-${c.contrato}`} value={`${c.productor} · ${c.contrato}`} />)}
              </datalist>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {CAMPOS.map((c) => (
                <div key={c.k} className={c.k === "nombre_emisor" || c.k === "nombre_receptor" || c.k === "cdc" ? "col-span-2" : ""}>
                  <Field label={c.label} type={c.type} value={draft[c.k]} conf={conf[c.k]} onChange={setF(c.k)} testid={`facturas-f-${c.k}`} />
                </div>
              ))}
            </div>

            <p className="mb-2 mt-4 text-xs font-bold text-[#4b6355]">Montos ({draft.moneda || "—"})</p>
            <div className="grid grid-cols-3 gap-3">
              {MONTOS.map((m) => (
                <Field key={m.k} label={m.label} value={draft[m.k]} conf={conf[m.k]} onChange={(v) => setF(m.k)(v === "" ? null : Number(v))} testid={`facturas-m-${m.k}`} />
              ))}
            </div>

            {/* Validaciones */}
            {vals.length > 0 && (
              <div className="mt-4 space-y-1.5" data-testid="facturas-validaciones">
                {vals.map((v, i) => (
                  <div key={i} className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium ${v.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                    {v.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />} {v.msg}
                  </div>
                ))}
              </div>
            )}

            <Button onClick={save} disabled={busy} className="mt-5 w-full gap-2 bg-[#14532d] hover:bg-[#0f3d21]" data-testid="facturas-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar factura
            </Button>
          </div>
        </div>
      )}

      {tab === "historial" && (
        <div className="rounded-2xl bg-white shadow-[0_2px_10px_rgba(20,83,45,0.08)]" data-testid="facturas-historial">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-[#14532d] text-white"><tr>
                {["Fecha", "N° factura", "Emisor", "Contrato", "Total", "Fuente", ""].map((h, i) => <th key={i} className={`px-3 py-2.5 font-semibold ${i === 4 ? "text-right" : "text-left"}`}>{h}</th>)}
              </tr></thead>
              <tbody>
                {hist.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">Todavía no cargaste facturas.</td></tr>
                ) : hist.map((f) => (
                  <tr key={f.id} className="border-t border-slate-100 hover:bg-[#f7faf6]" data-testid={`factura-row-${f.id}`}>
                    <td className="px-3 py-2">{f.fecha || "—"}</td>
                    <td className="px-3 py-2 font-mono">{f.numero || "—"}</td>
                    <td className="px-3 py-2">{f.nombre_emisor || "—"}</td>
                    <td className="px-3 py-2">{f.cliente || "—"} <span className="text-slate-400">· {f.contrato || "—"}</span></td>
                    <td className="px-3 py-2 text-right font-semibold">{NUM(f.total)} {f.moneda || ""}</td>
                    <td className="px-3 py-2"><span className="rounded-full bg-[#e4f7ea] px-2 py-0.5 text-[11px] font-bold text-[#14532d]">{f.fuente || "—"}</span></td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => delFactura(f.id)} className="text-red-500 hover:text-red-700" data-testid={`factura-del-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
